import { isValidElement } from "react";
import {
  USER_AVATAR_BACKGROUNDS,
  DEFAULT_EMOJI_DESCRIPTIONS,
} from "../constants/reportPDFs";
import { formatRadioListDisplayValue, isRadioListInputType } from "./radioList";

export const compareLocaleStrings = (valueA, valueB) =>
  String(valueA ?? "").localeCompare(String(valueB ?? ""), undefined, {
    sensitivity: "base",
    numeric: true,
  });

export const compareNumericValues = (valueA, valueB) =>
  (Number(valueA) || 0) - (Number(valueB) || 0);

const getInputResponseSortValue = (input) => {
  if (input?.sliderInfo?.value != null) {
    return String(input.sliderInfo.value);
  }

  const response = input?.userResponse;
  if (typeof response === "string") return response;
  if (isValidElement(response)) return "media response";

  return "";
};

export const getUserResponseSortValue = (record) => {
  if (record?.sliderInfo?.value != null) {
    return String(record.sliderInfo.value);
  }

  if (
    Array.isArray(record.responseInputs) &&
    record.responseInputs.length > 1
  ) {
    return record.responseInputs
      .map(getInputResponseSortValue)
      .filter(Boolean)
      .join(" ");
  }

  const response = record?.userResponse;
  if (typeof response === "string") return response;
  if (isValidElement(response)) return "media response";

  return "";
};

export const getUserAvatarBackground = (seed) => {
  if (!seed) {
    return "linear-gradient(135deg, #f9fafb, #6b7280)";
  }

  let hash = 0;
  const value = String(seed);

  for (let i = 0; i < value.length; i += 1) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }

  return USER_AVATAR_BACKGROUNDS[
    Math.abs(hash) % USER_AVATAR_BACKGROUNDS.length
  ];
};

export const getUserAvatarBackgroundByIndex = (index) => {
  if (index == null || Number.isNaN(index) || index < 0) {
    return "linear-gradient(135deg, #f9fafb, #6b7280)";
  }

  const hue = (index * 137.508) % 360;
  const hue2 = (hue + 24) % 360;

  return `linear-gradient(135deg, hsl(${hue.toFixed(1)}, 68%, 58%), hsl(${hue2.toFixed(1)}, 70%, 44%))`;
};

export const getRespondentLabelIndex = (label) => {
  const match = /(\d+)\s*$/.exec(String(label || ""));

  if (!match) {
    return null;
  }

  const num = Number(match[1]);

  return Number.isFinite(num) && num > 0 ? num - 1 : null;
};

const toExportResponseValue = (
  userResponse,
  sliderInfo,
  formatResponseValue,
) => {
  let responseText = formatResponseValue(userResponse, sliderInfo);

  if (typeof responseText === "string") {
    return responseText.trim();
  }

  if (responseText == null) {
    return "";
  }

  if (typeof responseText === "object") {
    return "";
  }

  return String(responseText);
};

export const EMPTY_EXPORT_VALUE = "-";
export const UNTRANSCRIBED_VIDEO_VALUE = "No transcription available";

export const MEDIA_INPUT_TYPES = new Set([
  "record-video",
  "video",
  "audio",
  "media",
]);

export const isMediaInputType = (inputType) =>
  MEDIA_INPUT_TYPES.has(
    String(inputType || "")
      .trim()
      .toLowerCase(),
  );

export const isRecordVideoInputType = (inputType) =>
  String(inputType || "")
    .trim()
    .toLowerCase() === "record-video";

export const isMediaStorageUrl = (value) => {
  if (value == null) return false;

  const text = String(value).trim();

  if (!text) return false;

  if (/^https?:\/\//i.test(text)) {
    return true;
  }

  return /\.(webm|mp4|mov|m4a|mp3|wav|ogg|avi|mkv)(\?|#|$)/i.test(text);
};

const TRANSCRIPTION_PLACEHOLDERS = new Set([
  "no transcription available",
  "no translation available",
  "no translation",
  "no video recorded",
  "no response",
  "[object object]",
  "-",
]);

const pickTranscriptionString = (value) => {
  if (typeof value !== "string") return null;

  const text = value.trim();
  if (!text) return null;
  if (isMediaStorageUrl(text)) return null;
  if (TRANSCRIPTION_PLACEHOLDERS.has(text.toLowerCase())) return null;

  return text;
};

export const isUsableTranscriptionText = (value) =>
  Boolean(pickTranscriptionString(value));

const collectTranscriptionText = (value, seen = new Set()) => {
  const direct = pickTranscriptionString(value);
  if (direct) return direct;

  if (value == null || typeof value !== "object") return null;
  if (seen.has(value)) return null;
  seen.add(value);

  if (Array.isArray(value)) {
    let found = null;

    value.forEach((item) => {
      const text = collectTranscriptionText(item, seen);
      if (text) found = text;
    });

    return found;
  }

  return (
    pickTranscriptionString(value.text) ||
    pickTranscriptionString(value.transcription) ||
    pickTranscriptionString(value.transcriptionText) ||
    pickTranscriptionString(value.transcript) ||
    pickTranscriptionString(value.translated_text) ||
    pickTranscriptionString(value.translation_text) ||
    pickTranscriptionString(value.content) ||
    collectTranscriptionText(value.translation, seen) ||
    collectTranscriptionText(value.translations, seen)
  );
};

export const extractTranslationFromAssignments = (assignments) => {
  if (!assignments) return null;

  const list = Array.isArray(assignments) ? assignments : [assignments];
  let found = null;

  list.forEach((assignment) => {
    if (!assignment || typeof assignment !== "object") return;

    const fromTranslation =
      collectTranscriptionText(assignment.translation) ||
      collectTranscriptionText(assignment.translations) ||
      pickTranscriptionString(assignment.transcriptionText) ||
      pickTranscriptionString(assignment.transcription) ||
      pickTranscriptionString(assignment.transcript);

    if (fromTranslation) {
      found = fromTranslation;
    }

    if (Array.isArray(assignment.logs)) {
      assignment.logs.forEach((log) => {
        const fromLog =
          pickTranscriptionString(log?.translation) ||
          collectTranscriptionText(log?.translation);

        if (fromLog) {
          found = fromLog;
        }
      });
    }
  });

  return found;
};

export const getRecordVideoTranscriptionText = (input = {}) =>
  extractTranscriptionText({
    transcription: input.transcriptionText ?? input.transcription,
    translation: input.translation,
    assignments: input.response_assignments || input.assignments,
    rawValue: input.rawValue,
  }) ||
  pickTranscriptionString(input.userResponse) ||
  pickTranscriptionString(input.exportValue);

export const resolveVideoExportResponse = (input = {}) => {
  const transcription = getRecordVideoTranscriptionText(input);

  if (transcription) {
    return transcription;
  }

  return UNTRANSCRIBED_VIDEO_VALUE;
};

export const isMissingRecordVideoTranscription = (input = {}) => {
  if (
    !isRecordVideoInputType(input.type) &&
    !isRecordVideoInputType(input.fieldType) &&
    !isRecordVideoInputType(input.input_type)
  ) {
    return false;
  }

  const hasMedia = Boolean(input.mediaUrl || extractMediaUrl(input.rawValue));

  if (!hasMedia) {
    return false;
  }

  return !getRecordVideoTranscriptionText(input);
};

export const countMissingRecordVideoTranscriptions = (rows = []) => {
  let count = 0;

  rows.forEach((row) => {
    const inputs =
      Array.isArray(row.responseInputs) && row.responseInputs.length > 0
        ? row.responseInputs
        : [
            {
              type: row.type,
              mediaUrl: row.mediaUrl,
              rawValue: row.rawExportValue ?? row.rawValue,
              transcriptionText: row.transcriptionText,
            },
          ];

    inputs.forEach((input) => {
      if (isMissingRecordVideoTranscription(input)) {
        count += 1;
      }
    });
  });

  return count;
};

export const extractMediaUrl = (rawValue) => {
  if (rawValue == null || rawValue === "") {
    return null;
  }

  if (Array.isArray(rawValue)) {
    for (const item of rawValue) {
      const url = extractMediaUrl(item);
      if (url) return url;
    }

    return null;
  }

  if (typeof rawValue === "string") {
    const text = rawValue.trim();
    return text.startsWith("http") ? text : null;
  }

  if (typeof rawValue === "object") {
    const candidates = [
      rawValue.url,
      rawValue.src,
      rawValue.mediaUrl,
      rawValue.videoUrl,
      rawValue.audioUrl,
      rawValue.file_url,
      rawValue.value,
    ];

    for (const candidate of candidates) {
      if (
        typeof candidate === "string" &&
        candidate.trim().startsWith("http")
      ) {
        return candidate.trim();
      }
    }
  }

  return null;
};

export const getFileResponseUrls = (rawValue) => {
  if (rawValue == null || rawValue === "") {
    return [];
  }

  if (Array.isArray(rawValue)) {
    return rawValue.flatMap((item) => getFileResponseUrls(item));
  }

  if (typeof rawValue === "string") {
    const text = rawValue.trim();
    const parts = text.split(/,\s*(?=https?:\/\/|data:)/i);

    if (parts.length > 1) {
      return parts.flatMap((part) => getFileResponseUrls(part.trim()));
    }
  }

  const url = extractMediaUrl(rawValue);
  return url ? [url] : [];
};

export const extractTranscriptionText = ({
  transcription,
  translation,
  assignments,
  rawValue,
} = {}) => {
  const fromDirect = collectTranscriptionText(transcription);
  if (fromDirect) {
    return fromDirect;
  }

  const fromTranslation = collectTranscriptionText(translation);
  if (fromTranslation) {
    return fromTranslation;
  }

  const fromAssignments = extractTranslationFromAssignments(assignments);
  if (fromAssignments) {
    return fromAssignments;
  }

  if (rawValue && typeof rawValue === "object") {
    const fromValue =
      collectTranscriptionText(rawValue.transcription) ||
      collectTranscriptionText(rawValue.transcriptionText) ||
      collectTranscriptionText(rawValue.transcript) ||
      collectTranscriptionText(rawValue.translation) ||
      collectTranscriptionText(rawValue.translations) ||
      pickTranscriptionString(rawValue.text);

    if (fromValue) {
      return fromValue;
    }
  }

  return pickTranscriptionString(rawValue);
};

export const serializeMediaForExport = ({
  mediaUrl = null,
  transcription = null,
  hasMedia = false,
} = {}) => {
  const transcript = pickTranscriptionString(transcription);

  if (transcript) {
    return transcript;
  }

  if (
    hasMedia ||
    (typeof mediaUrl === "string" && mediaUrl.trim().startsWith("http"))
  ) {
    return UNTRANSCRIBED_VIDEO_VALUE;
  }

  return EMPTY_EXPORT_VALUE;
};

export const getInternalRespondentKey = (row) => {
  if (row?.userId) {
    return String(row.userId);
  }

  const rawUserId = row?.rawData?.user?._id;
  if (rawUserId) {
    return String(rawUserId);
  }

  if (row?.email && row.email !== "N/A") {
    return `email:${String(row.email).trim().toLowerCase()}`;
  }

  if (row?.key) {
    return `row:${String(row.key)}`;
  }

  return `anon:${String(row?.name || "unknown")}`;
};

/**
 * Deterministic respondent labels: User001, User002, … User010, User100.
 * Unique respondents are sorted by internal key, then numbered with 3 digits.
 */
export const buildRespondentPseudonymMap = (rows = []) => {
  const uniqueKeys = [];
  const seen = new Set();

  rows.forEach((row) => {
    const key = getInternalRespondentKey(row);
    if (!key || seen.has(key)) {
      return;
    }

    seen.add(key);
    uniqueKeys.push(key);
  });

  uniqueKeys.sort((a, b) => compareLocaleStrings(a, b));

  const map = {};
  uniqueKeys.forEach((key, index) => {
    map[key] = `User${String(index + 1).padStart(3, "0")}`;
  });

  return map;
};

export const compareLongFormatRecords = (a, b) => {
  const respondentCompare = compareLocaleStrings(
    a.respondentId || a.user,
    b.respondentId || b.user,
  );
  if (respondentCompare !== 0) {
    return respondentCompare;
  }

  const questionCompare = compareNumericValues(
    a.questionNumber ?? a.Question_ID,
    b.questionNumber ?? b.Question_ID,
  );
  if (questionCompare !== 0) {
    return questionCompare;
  }

  return compareNumericValues(a.inputIndex, b.inputIndex);
};

const isEmptyExportValue = (value) =>
  value == null ||
  value === "" ||
  value === "No response" ||
  value === "No video recorded" ||
  value === "No translation available" ||
  value === "[object Object]";

const ANONYMOUS_EXPORT_LABELS = new Set([
  "anonymous",
  "anonymous user",
  "anonymous response",
]);

const normalizeConsentLabel = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const CONSENT_FIELD_LABELS = new Set([
  ...ANONYMOUS_EXPORT_LABELS,
  "terms",
  "terms accepted",
  "terms and conditions",
  "terms conditions",
]);

export const isConsentReportField = (value) => {
  const normalized = normalizeConsentLabel(value);

  if (!normalized) {
    return false;
  }

  if (CONSENT_FIELD_LABELS.has(normalized)) {
    return true;
  }

  return (
    normalized.endsWith(" terms and conditions") ||
    normalized.endsWith(" terms accepted")
  );
};

export const isAnonymousExportResponse = (value) => {
  if (value == null || value === "") {
    return false;
  }

  if (typeof value === "object") {
    if (isValidElement(value)) {
      return false;
    }

    const nested =
      value.selection ??
      value.label ??
      (value.value != null && typeof value.value !== "object"
        ? value.value
        : null) ??
      value.otherValue;

    if (nested != null && nested !== value) {
      return isAnonymousExportResponse(nested);
    }

    return false;
  }

  return isConsentReportField(value);
};

export const isAnonymousExportInput = (input = {}, ...answers) => {
  const title = input.inputTitle || input.title || "";
  const questionTitle = input.questionTitle || input.question || "";
  const type = String(
    input.fieldType || input.input_type || input.type || "",
  )
    .trim()
    .toLowerCase();

  if (
    isConsentReportField(title) ||
    isConsentReportField(questionTitle) ||
    type === "anonymous" ||
    type === "terms"
  ) {
    return true;
  }

  return answers.some((answer) => isAnonymousExportResponse(answer));
};

export const formatMatrixCellValue = (value) => {
  if (isEmptyExportValue(value)) {
    return EMPTY_EXPORT_VALUE;
  }

  if (typeof value === "object") {
    if (value?.value != null && typeof value.value !== "object") {
      const nestedValue = String(value.value).trim();
      return nestedValue || EMPTY_EXPORT_VALUE;
    }

    return EMPTY_EXPORT_VALUE;
  }

  const text = String(value).trim();
  return text || EMPTY_EXPORT_VALUE;
};

export const extractSliderNumericValue = (rawValue) => {
  if (rawValue == null || rawValue === "") {
    return null;
  }

  if (typeof rawValue === "number" && !Number.isNaN(rawValue)) {
    return rawValue;
  }

  if (typeof rawValue === "string") {
    const parsed = parseFloat(rawValue);
    return Number.isNaN(parsed) ? null : parsed;
  }

  if (typeof rawValue === "object") {
    if (rawValue.value != null) {
      return extractSliderNumericValue(rawValue.value);
    }
  }

  return null;
};

export const hasExplicitSliderSelection = (rawValue) => {
  if (rawValue == null || rawValue === "") {
    return false;
  }

  if (typeof rawValue === "object" && !Array.isArray(rawValue)) {
    const hasIndex =
      rawValue.emojiIndex != null &&
      String(rawValue.emojiIndex).trim() !== "" &&
      !Number.isNaN(Number(rawValue.emojiIndex));
    const hasLabel =
      rawValue.label != null && String(rawValue.label).trim() !== "";

    return hasIndex || hasLabel;
  }

  if (typeof rawValue === "number") {
    return !Number.isNaN(rawValue) && rawValue !== 0;
  }

  if (typeof rawValue === "string") {
    const trimmed = rawValue.trim();

    if (
      !trimmed ||
      trimmed === "0" ||
      trimmed === EMPTY_EXPORT_VALUE ||
      trimmed === "No response" ||
      trimmed === "No option selected"
    ) {
      return false;
    }

    return true;
  }

  return false;
};

export const isZeroBasedScaleOptions = (options = []) => {
  if (!Array.isArray(options) || options.length < 2) {
    return false;
  }

  return options.every((option, index) => {
    const text = String(option ?? "").trim();

    if (!text) return false;

    const num = Number(text);
    return Number.isInteger(num) && num === index;
  });
};

export const isStarSliderInput = (input = {}) => {
  if (input?.show_stars || input?.slider_config?.show_stars) {
    return true;
  }

  if (input?.sliderInfo?.show_stars) {
    return true;
  }

  const options =
    input?.input_options ||
    input?.inputOptions ||
    input?.slider_config?.options ||
    input?.sliderInfo?.inputOptions ||
    [];

  return isZeroBasedScaleOptions(options);
};

export const toOneBasedScaleOptions = (options = []) => {
  if (!isZeroBasedScaleOptions(options)) {
    return (options || [])
      .map((option) => {
        if (option == null) return "";
        if (typeof option === "string" || typeof option === "number") {
          return String(option).trim();
        }
        return "";
      })
      .filter(Boolean);
  }

  return options.map((_, index) => String(index + 1));
};

export const getSliderRatingIndex = (rawValue, totalOptions = 5) => {
  if (!hasExplicitSliderSelection(rawValue)) {
    return null;
  }

  const total = Math.max(Number(totalOptions) || 5, 1);

  if (rawValue && typeof rawValue === "object") {
    if (
      rawValue.emojiIndex != null &&
      !Number.isNaN(Number(rawValue.emojiIndex))
    ) {
      return Math.min(
        Math.max(Math.round(Number(rawValue.emojiIndex)), 0),
        total - 1,
      );
    }
  }

  const sliderValue = extractSliderNumericValue(rawValue);

  if (sliderValue == null) {
    return null;
  }

  return Math.min(
    Math.max(Math.round((sliderValue / 100) * (total - 1)), 0),
    total - 1,
  );
};

export const getSliderStarRating = (rawValue, totalOptions = 5) => {
  const index = getSliderRatingIndex(rawValue, totalOptions);

  return index == null ? null : index + 1;
};

export const formatSliderExportValue = (rawValue, inputField = {}) => {
  const sliderValue = extractSliderNumericValue(rawValue);

  if (sliderValue == null) {
    return EMPTY_EXPORT_VALUE;
  }

  const inputOptions = Array.isArray(inputField.input_options)
    ? inputField.input_options
    : Array.isArray(inputField.inputOptions)
      ? inputField.inputOptions
      : [];

  const totalOptions = Math.max(
    inputOptions.length ||
      Number(inputField.totalOptions) ||
      parseInt(inputField.scale_type || "5", 10) ||
      5,
    1,
  );

  const emojiIndex = getSliderRatingIndex(rawValue, totalOptions);

  if (emojiIndex == null) {
    return EMPTY_EXPORT_VALUE;
  }
  const useOneBased =
    isStarSliderInput(inputField) || isZeroBasedScaleOptions(inputOptions);

  if (useOneBased) {
    return String(emojiIndex + 1);
  }

  const descriptionsArray =
    DEFAULT_EMOJI_DESCRIPTIONS[String(totalOptions)] ||
    DEFAULT_EMOJI_DESCRIPTIONS["5"];
  const label =
    (inputOptions[emojiIndex] != null &&
    String(inputOptions[emojiIndex]).trim() &&
    !isZeroBasedScaleOptions(inputOptions)
      ? String(inputOptions[emojiIndex]).trim()
      : null) ||
    descriptionsArray?.[emojiIndex] ||
    `Rating ${emojiIndex + 1}`;

  const displayValue = `${Math.round(sliderValue)}/100`;

  return `${label} (${displayValue})`;
};

export const stringifyChoiceOption = (item) => {
  if (item == null || item === "") {
    return "";
  }

  if (
    typeof item === "string" ||
    typeof item === "number" ||
    typeof item === "boolean"
  ) {
    const text = String(item).trim();

    return text === "[object Object]" ? "" : text;
  }

  if (typeof item !== "object") {
    return "";
  }

  if (Array.isArray(item)) {
    return item
      .map((entry) => stringifyChoiceOption(entry))
      .filter(Boolean)
      .join(", ");
  }

  const candidates = [
    item.label,
    item.value,
    item.text,
    item.name,
    item.title,
    item.option,
    item.selection,
    item.otherValue,
  ];

  for (const candidate of candidates) {
    if (candidate == null || typeof candidate === "object") {
      continue;
    }

    const text = String(candidate).trim();

    if (text && text !== "[object Object]") {
      return text;
    }
  }

  return "";
};

const extractCheckboxLabels = (item) => {
  if (item == null || item === "") {
    return [];
  }

  if (
    typeof item === "string" ||
    typeof item === "number" ||
    typeof item === "boolean"
  ) {
    const label = stringifyChoiceOption(item);
    return label ? [label] : [];
  }

  if (typeof item !== "object") {
    return [];
  }

  if (Array.isArray(item)) {
    return [...new Set(item.flatMap(extractCheckboxLabels).filter(Boolean))];
  }

  const labels = [];

  if (Array.isArray(item.selections)) {
    labels.push(
      ...item.selections
        .map((selection) => stringifyChoiceOption(selection))
        .filter(Boolean),
    );
  }

  if (Array.isArray(item.value)) {
    labels.push(
      ...item.value
        .map((selection) => stringifyChoiceOption(selection))
        .filter(Boolean),
    );
  }

  const selectionLabel = stringifyChoiceOption(item.selection);

  if (selectionLabel) {
    labels.push(selectionLabel);
  }

  const otherLabel = stringifyChoiceOption(item.otherValue);

  if (otherLabel) {
    labels.push(otherLabel);
  }

  const explicitLabel = stringifyChoiceOption(item.label);

  if (explicitLabel) {
    labels.push(explicitLabel);
  }

  if (
    item.value != null &&
    typeof item.value !== "object" &&
    stringifyChoiceOption(item.value)
  ) {
    labels.push(stringifyChoiceOption(item.value));
  }

  if (labels.length === 0) {
    const selfLabel = stringifyChoiceOption(item);

    if (selfLabel) {
      labels.push(selfLabel);
    }
  }

  return labels.filter((label) => label && label !== "[object Object]");
};

export const formatCheckboxExportValue = (rawValue) => {
  if (rawValue == null || rawValue === "") {
    return EMPTY_EXPORT_VALUE;
  }

  if (Array.isArray(rawValue)) {
    const labels = [...new Set(rawValue.flatMap(extractCheckboxLabels))];
    return labels.length > 0 ? labels.join(", ") : EMPTY_EXPORT_VALUE;
  }

  const labels = extractCheckboxLabels(rawValue);
  const joined = labels.length > 0 ? labels.join(", ") : EMPTY_EXPORT_VALUE;
  return joined === "[object Object]" ? EMPTY_EXPORT_VALUE : joined;
};

export const CHECKBOX_YES = "Yes";
export const CHECKBOX_NO = "No";
export const CHOICE_CHECKED = "Checked";
export const CHOICE_UNCHECKED = "Unchecked";

export const isChoiceInputType = (inputType) => {
  const type = String(inputType || "")
    .trim()
    .toLowerCase();

  return type === "checkbox" || type === "radio" || type === "dropdown";
};

export const normalizeCheckboxOptions = (rawOptions) => {
  const cleanOption = (option) => {
    const text = stringifyChoiceOption(option);
    return text.replace(/^[\s"'[\]]+|[\s"'[\]]+$/g, "").trim();
  };

  const splitOptionText = (text) => {
    const trimmed = String(text ?? "").trim();

    if (!trimmed) return [];

    if (trimmed.includes("\n") || trimmed.includes("\r")) {
      return trimmed
        .split(/\r?\n/)
        .map((opt) => cleanOption(opt))
        .filter(Boolean);
    }

    if (trimmed.includes("|")) {
      return trimmed
        .split("|")
        .map((opt) => cleanOption(opt))
        .filter(Boolean);
    }

    const DECIMAL_COMMA = "\u0000";
    const protectedDecimals = trimmed.replace(
      /(\d),(\d)/g,
      `$1${DECIMAL_COMMA}$2`,
    );

    return protectedDecimals
      .split(",")
      .map((opt) => cleanOption(String(opt).split(DECIMAL_COMMA).join(",")))
      .filter(Boolean);
  };

  if (Array.isArray(rawOptions)) {
    if (
      rawOptions.length === 1 &&
      typeof rawOptions[0] === "string" &&
      /[,|\n]/.test(rawOptions[0])
    ) {
      return splitOptionText(rawOptions[0]);
    }

    return rawOptions.map((opt) => cleanOption(opt)).filter(Boolean);
  }

  if (typeof rawOptions === "string" && rawOptions.trim()) {
    return splitOptionText(rawOptions);
  }

  return [];
};

export const getCheckboxSelections = (rawValue) => {
  if (rawValue == null || rawValue === "") {
    return { selections: [], otherValue: null };
  }

  if (Array.isArray(rawValue)) {
    return {
      selections: [
        ...new Set(
          rawValue.flatMap(extractCheckboxLabels).map((label) => label.trim()),
        ),
      ].filter((label) => label && label !== "[object Object]"),
      otherValue: null,
    };
  }

  if (typeof rawValue === "object") {
    const selections = [];

    if (Array.isArray(rawValue.selections)) {
      selections.push(
        ...rawValue.selections
          .map((item) => stringifyChoiceOption(item))
          .filter(Boolean),
      );
    }

    const selectionLabel = stringifyChoiceOption(rawValue.selection);

    if (selectionLabel) {
      selections.push(selectionLabel);
    }

    if (selections.length === 0) {
      selections.push(...extractCheckboxLabels(rawValue));
    }

    const otherValue =
      rawValue.otherValue != null && stringifyChoiceOption(rawValue.otherValue)
        ? stringifyChoiceOption(rawValue.otherValue)
        : rawValue.value != null &&
            typeof rawValue.value !== "object" &&
            stringifyChoiceOption(rawValue.value) &&
            String(rawValue.selection || "")
              .trim()
              .toLowerCase() === "other"
          ? stringifyChoiceOption(rawValue.value)
          : null;

    return {
      selections: [...new Set(selections)].filter(
        (label) => label && label !== "[object Object]",
      ),
      otherValue,
    };
  }

  const label = stringifyChoiceOption(rawValue);
  return {
    selections: label ? [label] : [],
    otherValue: null,
  };
};

export const sanitizeCheckboxColumnToken = (option) => {
  const token = String(option || "")
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return token || "Option";
};

export const buildCheckboxOptionKey = (option) =>
  `cb_${sanitizeCheckboxColumnToken(option)}`;

/**
 * Expand a multi-choice input into one explicit Checked/Unchecked entry per option.
 * Every option gets its own column key (cb_OptionName); non-selections are Unchecked.
 */
export const expandCheckboxInputToOptionEntries = (input = {}) => {
  const { selections, otherValue } = getCheckboxSelections(
    input.rawValue != null ? input.rawValue : input.userResponse,
  );
  const selectedSet = new Set(
    selections.map((label) => String(label).trim().toLowerCase()),
  );

  if (otherValue) {
    selectedSet.add("other");
  }

  const optionList = [
    ...normalizeCheckboxOptions(input.inputOptions || input.input_options),
  ];

  selections.forEach((selection) => {
    if (
      !optionList.some(
        (option) =>
          String(option).trim().toLowerCase() ===
          String(selection).trim().toLowerCase(),
      )
    ) {
      optionList.push(selection);
    }
  });

  if (
    (otherValue || selectedSet.has("other")) &&
    !optionList.some(
      (option) => String(option).trim().toLowerCase() === "other",
    )
  ) {
    optionList.push("Other");
  }

  if (optionList.length === 0) {
    return [];
  }

  return optionList.map((option, optionIndex) => {
    const normalizedOption = String(option).trim();
    const isSelected =
      selectedSet.has(normalizedOption.toLowerCase()) ||
      (normalizedOption.toLowerCase() === "other" && Boolean(otherValue));

    return {
      option: normalizedOption,
      optionKey: buildCheckboxOptionKey(normalizedOption),
      optionIndex,
      response: isSelected ? CHOICE_CHECKED : CHOICE_UNCHECKED,
      // Keep Yes/No aliases for analytics consumers that expect them
      explicitValue: isSelected ? CHECKBOX_YES : CHECKBOX_NO,
      otherValue:
        isSelected && normalizedOption.toLowerCase() === "other"
          ? otherValue
          : null,
    };
  });
};

export const formatMatrixExportValue = (inputField, inputResponse) => {
  if (!inputResponse) {
    return EMPTY_EXPORT_VALUE;
  }

  const rawValue = inputResponse.value;
  const inputType = inputField?.input_type || inputResponse?.type || "";

  if (
    rawValue == null ||
    rawValue === "" ||
    (Array.isArray(rawValue) && rawValue.length === 0)
  ) {
    return EMPTY_EXPORT_VALUE;
  }

  switch (inputType) {
    case "checkbox":
    case "radio":
    case "dropdown":
      return formatCheckboxExportValue(rawValue);
    case "checkbox-list":
    case "radio-list":
      return formatRadioListDisplayValue(rawValue) || EMPTY_EXPORT_VALUE;
    case "record-video": {
      const transcription = extractTranscriptionText({
        transcription:
          inputResponse?.transcriptionText || inputResponse?.transcription,
        translation: inputResponse?.translation,
        assignments: inputResponse?.response_assignments,
        rawValue,
      });

      if (transcription) {
        return transcription;
      }

      const mediaUrl = extractMediaUrl(rawValue);
      return serializeMediaForExport({
        mediaUrl,
        transcription: null,
        hasMedia: Boolean(mediaUrl) || Boolean(rawValue),
      });
    }
    case "video":
    case "audio":
    case "media": {
      const mediaUrl = extractMediaUrl(rawValue);
      const transcription = extractTranscriptionText({
        transcription: inputResponse?.transcription,
        translation: inputResponse?.translation,
        rawValue,
      });

      return serializeMediaForExport({
        mediaUrl,
        transcription,
        hasMedia: Boolean(mediaUrl) || Boolean(rawValue),
      });
    }
    case "slider": {
      return formatSliderExportValue(rawValue, inputField);
    }
    case "paragraph":
    case "textarea":
      if (typeof rawValue === "string") {
        return rawValue.startsWith("http") ? EMPTY_EXPORT_VALUE : rawValue;
      }

      return EMPTY_EXPORT_VALUE;
    case "file":
    case "multiple-files":
      return getFileResponseUrls(rawValue).join(", ") || EMPTY_EXPORT_VALUE;
    default:
      return formatMatrixCellValue(rawValue);
  }
};

export const buildQuestionNumberMap = (formQuestions = []) => {
  const uniqueQuestionNumbers = [
    ...new Set(
      formQuestions
        .filter((question) => question?.number != null)
        .map((question) => question.number),
    ),
  ].sort((a, b) => Number(a) - Number(b));

  const questionNumberMap = {};
  uniqueQuestionNumbers.forEach((num, index) => {
    questionNumberMap[num] = index + 1;
  });

  return questionNumberMap;
};

const getInputTitleLabel = (inputTitle, inputIndex) => {
  const label = String(inputTitle || "").trim();

  if (label) {
    return label;
  }

  return `Input ${inputIndex + 1}`;
};

const buildQuestionColumnSchema = (
  formQuestions = [],
  rows = [],
  questionNumberMap = {},
) => {
  if (Array.isArray(formQuestions) && formQuestions.length > 0) {
    return [...formQuestions]
      .filter((question) => question?.number != null)
      .sort((a, b) => Number(a.number) - Number(b.number))
      .map((question) => {
        const sequentialNumber =
          questionNumberMap[question.number] ?? question.number;
        const fields = Array.isArray(question.response)
          ? question.response
          : [];
        const inputs = fields.map((field, inputIndex) => ({
          columnKey: `${sequentialNumber}-${inputIndex}`,
          inputIndex,
          subHeader: getInputTitleLabel(field.input_title, inputIndex),
        }));

        return {
          questionNumber: sequentialNumber,
          questionTitle: question.title || `Question ${sequentialNumber}`,
          groupHeader: `Question ${sequentialNumber}`,
          inputs:
            inputs.length > 0
              ? inputs
              : [
                  {
                    columnKey: `${sequentialNumber}-0`,
                    inputIndex: 0,
                    subHeader: "Input 1",
                  },
                ],
        };
      });
  }

  const questionsByNumber = new Map();

  rows.forEach((row) => {
    if (questionsByNumber.has(row.questionNumber)) {
      return;
    }

    const responseInputs =
      Array.isArray(row.responseInputs) && row.responseInputs.length > 0
        ? row.responseInputs
        : [
            {
              title: "",
              type: row.type,
            },
          ];

    questionsByNumber.set(row.questionNumber, {
      questionNumber: row.questionNumber,
      questionTitle: row.questionTitle,
      groupHeader: `Question ${row.questionNumber}`,
      inputs: responseInputs.map((input, inputIndex) => ({
        columnKey: `${row.questionNumber}-${inputIndex}`,
        inputIndex,
        subHeader: getInputTitleLabel(
          input.inputTitle || input.title,
          inputIndex,
        ),
      })),
    });
  });

  return [...questionsByNumber.values()].sort((a, b) =>
    compareNumericValues(a.questionNumber, b.questionNumber),
  );
};

export const buildUserMatrixExportData = (
  rows = [],
  formatResponseValue,
  formQuestions = [],
  questionNumberMap = {},
) => {
  const questionSchema = buildQuestionColumnSchema(
    formQuestions,
    rows,
    questionNumberMap,
  );
  const users = new Map();

  rows.forEach((row) => {
    const userKey = String(row.userId || `${row.name}::${row.email}`);

    if (!users.has(userKey)) {
      users.set(userKey, {
        name: row.name,
        email: row.email,
        values: {},
      });
    }

    const user = users.get(userKey);
    const responseInputs =
      Array.isArray(row.responseInputs) && row.responseInputs.length > 0
        ? row.responseInputs
        : [
            {
              userResponse: row.userResponse,
              sliderInfo: row.sliderInfo || null,
            },
          ];

    responseInputs.forEach((input, inputIndex) => {
      const columnKey = `${row.questionNumber}-${inputIndex}`;
      let value = EMPTY_EXPORT_VALUE;

      if (input.exportValue != null && input.exportValue !== "") {
        value = formatMatrixCellValue(input.exportValue);
      } else if (
        input.userResponse &&
        input.userResponse !== "No response" &&
        input.userResponse !== "No video recorded" &&
        input.userResponse !== EMPTY_EXPORT_VALUE
      ) {
        value = formatMatrixCellValue(
          toExportResponseValue(
            input.userResponse,
            input.sliderInfo,
            formatResponseValue,
          ),
        );
      }

      user.values[columnKey] = value;
    });
  });

  const questionHeaderRow = ["User"];
  const pdfQuestionHeaderRow = ["User"];
  const inputHeaderRow = [""];

  questionSchema.forEach((question) => {
    question.inputs.forEach((input, inputIndex) => {
      questionHeaderRow.push(inputIndex === 0 ? question.groupHeader : "");
      inputHeaderRow.push(input.subHeader);
    });

    if (question.inputs.length > 1) {
      pdfQuestionHeaderRow.push({
        content: question.groupHeader,
        colSpan: question.inputs.length,
        styles: { halign: "center", valign: "middle" },
      });
    } else {
      pdfQuestionHeaderRow.push({
        content: question.groupHeader,
        styles: { halign: "center", valign: "middle" },
      });
    }
  });

  const bodyRows = [...users.values()]
    .sort((a, b) => compareLocaleStrings(a.name, b.name))
    .map((user) => [
      user.name,
      ...questionSchema.flatMap((question) =>
        question.inputs.map(
          (input) => user.values[input.columnKey] || EMPTY_EXPORT_VALUE,
        ),
      ),
    ]);

  return {
    headerRows: [questionHeaderRow, inputHeaderRow],
    pdfHead: [pdfQuestionHeaderRow, inputHeaderRow],
    bodyRows,
    columnCount: inputHeaderRow.length,
  };
};

export const buildUserMatrixCsvRows = (
  rows = [],
  formatResponseValue,
  formQuestions = [],
  questionNumberMap = {},
) => {
  const { headerRows, bodyRows } = buildUserMatrixExportData(
    rows,
    formatResponseValue,
    formQuestions,
    questionNumberMap,
  );

  return {
    headerRows,
    bodyRows,
  };
};

const serializeLongFormatResponse = (input, formatResponseValue) => {
  // record-video: always export transcriptionText (never URL / placeholder when present)
  if (isRecordVideoInputType(input?.type) || isMediaInputType(input?.type)) {
    return resolveVideoExportResponse(input);
  }

  // One row per choice input: response = selected option label(s)
  if (isChoiceInputType(input?.type)) {
    const choiceValue = formatCheckboxExportValue(
      input.rawValue != null ? input.rawValue : input.userResponse,
    );
    if (choiceValue !== EMPTY_EXPORT_VALUE) {
      return choiceValue;
    }
  }

  if (
    String(input?.type || "")
      .trim()
      .toLowerCase() === "slider"
  ) {
    if (input?.sliderInfo && typeof formatResponseValue === "function") {
      const sliderText = toExportResponseValue(
        input.userResponse,
        input.sliderInfo,
        formatResponseValue,
      );
      const formatted = formatMatrixCellValue(sliderText);

      if (formatted !== EMPTY_EXPORT_VALUE && formatted !== "[object Object]") {
        return formatted;
      }
    }

    const fromHelper = formatSliderExportValue(input.rawValue, {
      input_options: input.inputOptions,
      inputOptions: input.inputOptions,
      totalOptions: input.sliderInfo?.totalOptions,
      scale_type: input.sliderInfo?.totalOptions,
    });

    if (fromHelper !== EMPTY_EXPORT_VALUE) {
      return fromHelper;
    }
  }

  if (input?.exportValue != null && input.exportValue !== "") {
    const exportValue = formatMatrixCellValue(input.exportValue);
    if (
      exportValue !== EMPTY_EXPORT_VALUE &&
      exportValue !== "[object Object]"
    ) {
      return exportValue;
    }
  }

  if (
    input?.userResponse == null ||
    input.userResponse === "" ||
    input.userResponse === "No response" ||
    input.userResponse === "No video recorded" ||
    input.userResponse === EMPTY_EXPORT_VALUE ||
    isValidElement(input.userResponse)
  ) {
    if (input?.sliderInfo && typeof formatResponseValue === "function") {
      const sliderText = toExportResponseValue(
        input.userResponse,
        input.sliderInfo,
        formatResponseValue,
      );
      return formatMatrixCellValue(sliderText);
    }

    return EMPTY_EXPORT_VALUE;
  }

  if (typeof formatResponseValue === "function") {
    const formatted = formatMatrixCellValue(
      toExportResponseValue(
        input.userResponse,
        input.sliderInfo,
        formatResponseValue,
      ),
    );

    return formatted === "[object Object]" ? EMPTY_EXPORT_VALUE : formatted;
  }

  const fallback = formatMatrixCellValue(input.userResponse);

  return fallback === "[object Object]" ? EMPTY_EXPORT_VALUE : fallback;
};

/**
 * Long-format rows:
 * User, question, input field name, response
 * One row per user + question + input (input field name).
 * Checkbox / radio / dropdown / slider each stay on one row; response is the
 * selected value(s) (or slider rating), not one row per option.
 *
 * Sort: Respondent_ID ascending, then Question_ID ascending (then input index).
 */
export const buildLongFormatRecords = (
  rows = [],
  formatResponseValue,
  respondentIdMap = null,
) => {
  const records = [];
  const pseudonymMap = respondentIdMap || buildRespondentPseudonymMap(rows);

  rows.forEach((row, rowIndex) => {
    const respondentKey = getInternalRespondentKey(row);
    const respondentId =
      row.respondentId || pseudonymMap[respondentKey] || "User000";
    const question = `question ${row.questionNumber}`;
    const responseInputs =
      Array.isArray(row.responseInputs) && row.responseInputs.length > 0
        ? row.responseInputs
        : [
            {
              title: row.questionTitle || "",
              inputTitle: row.questionTitle || "",
              userResponse: row.userResponse,
              sliderInfo: row.sliderInfo || null,
              exportValue: row.exportValue,
              type: row.type,
              rawValue: row.rawExportValue,
              inputOptions: row.inputOptions,
              transcriptionText: row.transcriptionText,
              translation: row.translation,
              response_assignments:
                row.response_assignments || row.rawData?.response_assignments,
            },
          ];

    responseInputs.forEach((input, inputIndex) => {
      const subquestion =
        String(input.inputTitle || input.title || "").trim() ||
        EMPTY_EXPORT_VALUE;
      const response = serializeLongFormatResponse(input, formatResponseValue);

      if (
        isAnonymousExportInput(
          input,
          response,
          input.userResponse,
          input.exportValue,
          input.rawValue,
        )
      ) {
        return;
      }

      if (isRecordVideoInputType(input?.type) || isMediaInputType(input?.type)) {
        if (!isUsableTranscriptionText(response)) {
          return;
        }
      }

      // Skip empty placeholder inputs for multi-input questions
      if (
        responseInputs.length > 1 &&
        (response === EMPTY_EXPORT_VALUE || response === "No response")
      ) {
        return;
      }

      records.push({
        key: `${row.key || rowIndex}-${input.key || inputIndex}`,
        respondentId,
        respondentKey,
        user: respondentId,
        question,
        questionNumber: Number(row.questionNumber) || 0,
        inputIndex,
        subquestion,
        response,
        userResponse: input.userResponse,
        type: input.type || row.type || "",
        email: row.email,
        avatar: row.avatar,
        userId: row.userId,
        avatarBackground: row.avatarBackground,
        rawData: row.rawData,
        sliderInfo: input.sliderInfo || null,
        rawValue: input.rawValue,
        inputOptions: input.inputOptions,
        mediaUrl: input.mediaUrl || null,
        transcriptionText: input.transcriptionText || null,
      });
    });
  });

  return records.sort(compareLongFormatRecords);
};

export const buildLongFormatCsvRows = (
  rows = [],
  formatResponseValue,
  respondentIdMap = null,
) => {
  const headers = ["User", "question", "input field name", "response"];
  const records = buildLongFormatRecords(
    rows,
    formatResponseValue,
    respondentIdMap,
  );
  const bodyRows = records.map((record) => [
    record.user,
    record.question,
    record.subquestion || EMPTY_EXPORT_VALUE,
    record.response,
  ]);

  return {
    headers,
    bodyRows,
    records,
    csvRows: [headers, ...bodyRows],
  };
};

export const mapExportInputType = (inputType) => {
  const type = String(inputType || "")
    .trim()
    .toLowerCase();

  switch (type) {
    case "media":
      return "video";
    case "record-video":
      return "video";
    default:
      return type || "text";
  }
};

export const formatExportInputTypeLabel = (inputType) => {
  const type = String(inputType || "")
    .trim()
    .toLowerCase()
    .replace(/[_]+/g, "-");

  const labels = {
    "radio-list": "Radio List",
    "checkbox-list": "Checkbox List",
    "multiple-files": "Multiple Files",
    "record-video": "Video",
    "record video": "Video",
  };

  if (labels[type]) {
    return labels[type];
  }

  return (
    type
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ") || "Text"
  );
};

export const formatExportResponseTime = (value) => {
  if (value == null || value === "") {
    return "";
  }

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  const pad = (n) => String(n).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const getOptionListForExportInput = (input = {}) => {
  const normalizedType = String(
    input.type || input.input_type || input.fieldType || "",
  )
    .trim()
    .toLowerCase();

  const asLabels = (list) => {
    if (list == null || list === "") {
      return [];
    }

    if (!Array.isArray(list)) {
      return normalizeCheckboxOptions(list);
    }

    if (
      list.length === 1 &&
      typeof list[0] === "string" &&
      (/[,\n]/.test(list[0]) || list[0].includes("|"))
    ) {
      return list[0]
        .split(/[,|\n]/)
        .map((opt) =>
          String(opt || "")
            .replace(/^[\s"'[\]]+|[\s"'[\]]+$/g, "")
            .trim(),
        )
        .filter(Boolean);
    }

    return list.map((option) => stringifyChoiceOption(option)).filter(Boolean);
  };

  const candidates = [
    input.input_options,
    input.inputOptions,
    input.options,
    input.slider_config?.options,
    input.sliderInfo?.inputOptions,
  ];

  let options = [];
  for (const candidate of candidates) {
    const parsed = asLabels(candidate);
    if (parsed.length > 0) {
      options = parsed;
      break;
    }
  }

  if (
    [
      "slider",
      "select",
      "radio",
      "dropdown",
      "checkbox",
      "multiselect",
      "multi-select",
    ].includes(normalizedType) &&
    options.length === 0
  ) {
    const scalePoints =
      input.slider_config?.scale_points ||
      input.sliderInfo?.totalOptions ||
      parseInt(input.scale_type || "5", 10);
    if (normalizedType === "slider") {
      if (isStarSliderInput(input)) {
        const total = Math.max(Number(scalePoints) || 5, 1);
        options = Array.from({ length: total }, (_, index) =>
          String(index + 1),
        );
      } else {
        options =
          DEFAULT_EMOJI_DESCRIPTIONS[String(scalePoints)] ||
          DEFAULT_EMOJI_DESCRIPTIONS["5"] ||
          [];
      }
    }
  }

  if (normalizedType === "slider") {
    if (isStarSliderInput(input) || isZeroBasedScaleOptions(options)) {
      const total =
        options.length ||
        Number(input.slider_config?.scale_points) ||
        Number(input.sliderInfo?.totalOptions) ||
        parseInt(input.scale_type || "5", 10) ||
        5;

      return Array.from({ length: Math.max(total, 1) }, (_, index) =>
        String(index + 1),
      );
    }
  }

  return options.map((option) => stringifyChoiceOption(option)).filter(Boolean);
};

const resolveOptionsFromFormQuestions = (
  row = {},
  input = {},
  formQuestions = [],
) => {
  if (!Array.isArray(formQuestions) || formQuestions.length === 0) {
    return [];
  }

  const inputId = input.inputId != null ? String(input.inputId) : null;
  const inputType = String(
    input.fieldType || input.type || input.input_type || "",
  )
    .trim()
    .toLowerCase();
  const inputTitle = String(input.inputTitle || input.title || "")
    .trim()
    .toLowerCase();

  // Strongest match: find the field by id across the whole form definition.
  if (inputId) {
    for (const question of formQuestions) {
      const fields = Array.isArray(question?.response) ? question.response : [];
      const match = fields.find((field) => String(field?._id) === inputId);

      if (match) {
        return getOptionListForExportInput({
          type: match.input_type,
          input_type: match.input_type,
          input_options: match.input_options,
          inputOptions: match.input_options,
          options: match.options,
          slider_config: match.slider_config,
          scale_type: match.scale_type,
        });
      }
    }
  }

  const questionId =
    row.rawData?.question?._id ||
    row.rawData?.questionId ||
    row.questionId ||
    null;
  const originalQuestionNumber = row.rawData?.question?.number;
  const exportQuestionNumber = Number(row.questionNumber);
  const questionNumberMap = buildQuestionNumberMap(formQuestions);

  const question =
    formQuestions.find(
      (q) => questionId && String(q._id) === String(questionId),
    ) ||
    formQuestions.find(
      (q) =>
        originalQuestionNumber != null &&
        Number(q.number) === Number(originalQuestionNumber),
    ) ||
    formQuestions.find(
      (q) =>
        Number.isFinite(exportQuestionNumber) &&
        Number(questionNumberMap[q.number]) === exportQuestionNumber,
    ) ||
    formQuestions.find((q) => Number(q.number) === exportQuestionNumber) ||
    null;

  if (!question || !Array.isArray(question.response)) {
    return [];
  }

  const match =
    question.response.find(
      (field) => inputId && String(field._id) === inputId,
    ) ||
    question.response.find(
      (field) =>
        inputTitle &&
        String(field.input_title || "")
          .trim()
          .toLowerCase() === inputTitle,
    ) ||
    question.response.find(
      (field) =>
        String(field.input_type || "")
          .trim()
          .toLowerCase() === inputType,
    ) ||
    question.response[0];

  if (!match) return [];

  return getOptionListForExportInput({
    type: match.input_type,
    input_type: match.input_type,
    input_options: match.input_options,
    inputOptions: match.input_options,
    options: match.options,
    slider_config: match.slider_config,
    scale_type: match.scale_type,
    show_stars: match.show_stars || match.slider_config?.show_stars,
    show_only_emojis:
      match.show_only_emojis || match.slider_config?.show_only_emojis,
  });
};

const resolveExportOptionList = (row = {}, input = {}, formQuestions = []) => {
  const fromForm = resolveOptionsFromFormQuestions(row, input, formQuestions);

  if (fromForm.length > 0) {
    return fromForm;
  }

  return getOptionListForExportInput(input);
};

const getSelectedOptionSet = (input = {}, optionList = []) => {
  const selectedSet = new Set();
  const optionLookup = new Map(
    optionList.map((option) => [String(option).trim().toLowerCase(), option]),
  );

  let otherValue = null;

  const addSelection = (value) => {
    if (value == null || value === "") return;

    if (typeof value === "object") {
      if (Array.isArray(value)) {
        value.forEach(addSelection);
        return;
      }

      const objectLabel = stringifyChoiceOption(value);

      if (objectLabel) {
        addSelection(objectLabel);
      }

      return;
    }

    const raw = stringifyChoiceOption(value);

    if (!raw || raw === "[object Object]") return;

    const direct = optionLookup.get(raw.toLowerCase());

    if (direct) {
      selectedSet.add(String(direct).trim().toLowerCase());
      return;
    }

    if (raw.includes(",")) {
      raw.split(",").forEach((part) => addSelection(part.trim()));
      return;
    }

    selectedSet.add(raw.toLowerCase());
  };

  const raw =
    input.rawValue != null
      ? input.rawValue
      : input.userResponse != null
        ? input.userResponse
        : null;

  if (raw != null && raw !== "") {
    if (Array.isArray(raw)) {
      raw.forEach(addSelection);
    } else if (typeof raw === "object") {
      if (Array.isArray(raw.selections)) {
        raw.selections.forEach(addSelection);
      }

      if (raw.selection != null) {
        addSelection(raw.selection);
      }

      if (raw.label != null) {
        addSelection(raw.label);
      }

      if (raw.value != null && typeof raw.value !== "object") {
        addSelection(raw.value);
      }

      if (Array.isArray(raw.value)) {
        raw.value.forEach(addSelection);
      }

      if (raw.otherValue != null && String(raw.otherValue).trim()) {
        otherValue = String(raw.otherValue).trim();
        selectedSet.add("other");
      }
    } else {
      addSelection(raw);
    }
  }

  if (selectedSet.size === 0) {
    const { selections, otherValue: parsedOther } = getCheckboxSelections(
      input.rawValue != null ? input.rawValue : input.userResponse,
    );
    selections.forEach(addSelection);

    if (parsedOther) {
      otherValue = parsedOther;
      selectedSet.add("other");
    }
  }

  if (selectedSet.size === 0) {
    if (
      typeof input.userResponse === "string" &&
      input.userResponse !== EMPTY_EXPORT_VALUE &&
      input.userResponse !== "No response"
    ) {
      addSelection(input.userResponse);
    } else if (
      typeof input.exportValue === "string" &&
      input.exportValue !== EMPTY_EXPORT_VALUE
    ) {
      addSelection(input.exportValue);
    }
  }

  if (otherValue) {
    selectedSet.add("other");
  }

  return { selectedSet, otherValue };
};

const getSimpleExportCellValue = (input = {}, formatResponseValue) => {
  const type = String(input.type || input.fieldType || input.input_type || "")
    .trim()
    .toLowerCase();

  if (isMediaInputType(type) || type === "record-video") {
    return resolveVideoExportResponse(input);
  }

  if (type === "slider") {
    const totalOptions = Math.max(
      (Array.isArray(input.inputOptions) && input.inputOptions.length) ||
        (Array.isArray(input.input_options) && input.input_options.length) ||
        Number(input.sliderInfo?.totalOptions) ||
        Number(input.slider_config?.scale_points) ||
        parseInt(input.scale_type || "5", 10) ||
        5,
      1,
    );

    if (isStarSliderInput(input)) {
      const starRating = getSliderStarRating(input.rawValue, totalOptions);

      return starRating == null ? "" : starRating;
    }

    const sliderValue = extractSliderNumericValue(input.rawValue);
    if (sliderValue == null) {
      return "";
    }

    const normalized = Math.round((sliderValue / 100) * 100) / 100;
    return normalized;
  }

  if (type === "file" || type === "multiple-files") {
    const urls = getFileResponseUrls(
      input.mediaUrl || input.rawValue || input.userResponse,
    );
    return urls.join(", ");
  }

  if (isRadioListInputType(type)) {
    return formatRadioListDisplayValue(
      input.rawValue ?? input.userResponse ?? input.exportValue,
    );
  }

  if (type === "date") {
    const raw = input.rawValue;

    if (raw == null || raw === "") return "";

    if (typeof raw === "string") {
      const parsed = new Date(raw);

      if (!Number.isNaN(parsed.getTime())) {
        const pad = (n) => String(n).padStart(2, "0");
        return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
      }

      return raw;
    }

    return String(raw);
  }

  if (typeof formatResponseValue === "function" && input.sliderInfo) {
    const formatted = formatMatrixCellValue(
      toExportResponseValue(
        input.userResponse,
        input.sliderInfo,
        formatResponseValue,
      ),
    );

    if (formatted && formatted !== EMPTY_EXPORT_VALUE) {
      return formatted;
    }
  }

  if (
    input.exportValue != null &&
    input.exportValue !== "" &&
    input.exportValue !== EMPTY_EXPORT_VALUE
  ) {
    const exportValue = formatMatrixCellValue(input.exportValue);

    if (isMediaStorageUrl(exportValue)) {
      return UNTRANSCRIBED_VIDEO_VALUE;
    }

    return exportValue;
  }

  if (typeof input.userResponse === "string") {
    if (
      !input.userResponse ||
      input.userResponse === EMPTY_EXPORT_VALUE ||
      input.userResponse === "No response" ||
      input.userResponse === "No video recorded" ||
      input.userResponse === UNTRANSCRIBED_VIDEO_VALUE
    ) {
      return "";
    }

    if (isMediaStorageUrl(input.userResponse)) {
      return UNTRANSCRIBED_VIDEO_VALUE;
    }

    return input.userResponse;
  }

  if (input.rawValue != null && typeof input.rawValue !== "object") {
    if (isMediaStorageUrl(input.rawValue)) {
      return UNTRANSCRIBED_VIDEO_VALUE;
    }

    return String(input.rawValue);
  }

  return "";
};

const getSelectedSliderOption = (input = {}, optionList = []) => {
  if (!optionList.length) return null;

  const raw = input.rawValue;
  const totalOptions = optionList.length;

  if (typeof raw === "string") {
    const trimmed = raw.trim();

    if (!trimmed) return null;

    const exact = optionList.find(
      (option) => String(option).trim().toLowerCase() === trimmed.toLowerCase(),
    );

    if (exact) return String(exact).trim();

    if (!/^\d+(\.\d+)?$/.test(trimmed)) {
      return null;
    }
  }

  const ratingIndex = getSliderRatingIndex(raw, totalOptions);

  if (ratingIndex != null && optionList[ratingIndex] != null) {
    return String(optionList[ratingIndex]).trim();
  }

  if (raw && typeof raw === "object") {
    const label =
      raw.label != null && String(raw.label).trim()
        ? String(raw.label).trim()
        : null;

    if (label) {
      if (
        /^\d+$/.test(label) &&
        isZeroBasedScaleOptions(
          input.input_options ||
            input.inputOptions ||
            input.slider_config?.options ||
            [],
        )
      ) {
        const zeroBased = Number(label);
        const oneBasedLabel = String(zeroBased + 1);
        const oneBasedMatch = optionList.find(
          (option) =>
            String(option).trim().toLowerCase() === oneBasedLabel.toLowerCase(),
        );
        if (oneBasedMatch) return String(oneBasedMatch).trim();
      }

      const match = optionList.find(
        (option) => String(option).trim().toLowerCase() === label.toLowerCase(),
      );

      if (match) return String(match).trim();

      return null;
    }
  }

  return null;
};

export const buildExampleFormatCsvRows = (
  rows = [],
  formatResponseValue,
  respondentIdMap = null,
  formQuestions = [],
) => {
  const headers = [
    "respondent_id",
    "question_id",
    "question",
    "input_type",
    "response_option",
    "response",
  ];
  const pseudonymMap = respondentIdMap || buildRespondentPseudonymMap(rows);
  const bodyRows = [];

  const sortedRows = [...rows].sort((a, b) => {
    const userCompare = compareLocaleStrings(
      a.respondentId || pseudonymMap[getInternalRespondentKey(a)] || "",
      b.respondentId || pseudonymMap[getInternalRespondentKey(b)] || "",
    );
    if (userCompare !== 0) return userCompare;
    return compareNumericValues(a.questionNumber, b.questionNumber);
  });

  sortedRows.forEach((row) => {
    const respondentKey = getInternalRespondentKey(row);
    const respondentId =
      row.respondentId || pseudonymMap[respondentKey] || "User000";
    const questionId = `q${row.questionNumber}`;
    const questionTitle = row.questionTitle || "";

    if (isConsentReportField(questionTitle)) {
      return;
    }

    const responseInputs =
      Array.isArray(row.responseInputs) && row.responseInputs.length > 0
        ? row.responseInputs
        : [
            {
              title: row.questionTitle || "",
              userResponse: row.userResponse,
              sliderInfo: row.sliderInfo || null,
              exportValue: row.exportValue,
              type: row.type,
              fieldType: row.type,
              rawValue: row.rawExportValue ?? row.rawValue,
              inputOptions: row.inputOptions,
              input_options: row.input_options,
              slider_config: row.slider_config,
              transcriptionText: row.transcriptionText,
              translation: row.translation,
              response_assignments:
                row.response_assignments || row.rawData?.response_assignments,
            },
          ];

    responseInputs.forEach((input) => {
      if (
        isAnonymousExportInput(
          input,
          input.userResponse,
          input.exportValue,
          input.rawValue,
        )
      ) {
        return;
      }

      const responseType = String(input.type || "")
        .trim()
        .toLowerCase();
      const fieldType = String(
        input.fieldType || input.input_type || input.type || "",
      )
        .trim()
        .toLowerCase();
      const normalizedType = fieldType || responseType;
      const exportType = mapExportInputType(normalizedType);

      let optionList = resolveExportOptionList(row, input, formQuestions);

      const isOptionExpandable =
        normalizedType === "slider" ||
        exportType === "slider" ||
        normalizedType === "checkbox" ||
        normalizedType === "radio" ||
        normalizedType === "dropdown" ||
        normalizedType === "select" ||
        normalizedType === "multiselect" ||
        normalizedType === "multi-select" ||
        exportType === "select" ||
        exportType === "multiselect";

      const isSlider = normalizedType === "slider" || exportType === "slider";

      if (isOptionExpandable && optionList.length > 0) {
        let selectedSet = new Set();
        let otherValue = null;

        if (isSlider) {
          const selectedOption = getSelectedSliderOption(input, optionList);
          if (selectedOption) {
            selectedSet.add(selectedOption.toLowerCase());
          }
        } else {
          ({ selectedSet, otherValue } = getSelectedOptionSet(
            input,
            optionList,
          ));
        }

        const hasMatchedOption =
          Boolean(otherValue) ||
          selectedSet.has("other") ||
          optionList.some((option) =>
            selectedSet.has(String(option).trim().toLowerCase()),
          );

        if (!hasMatchedOption) {
          bodyRows.push([
            respondentId,
            questionId,
            questionTitle,
            exportType,
            null,
            null,
          ]);
          return;
        }

        const rowsOptions = [...optionList];
        if (
          !isSlider &&
          (otherValue || selectedSet.has("other")) &&
          !rowsOptions.some(
            (option) => String(option).trim().toLowerCase() === "other",
          )
        ) {
          rowsOptions.push("Other");
        }

        rowsOptions.forEach((option) => {
          const optionLabel = String(option).trim();
          const optionKey = optionLabel.toLowerCase();
          const isOther = optionKey === "other";
          const isSelected =
            selectedSet.has(optionKey) || (isOther && Boolean(otherValue));

          let responseAnswer = null;

          if (isSelected) {
            responseAnswer =
              isOther && otherValue ? String(otherValue).trim() : optionLabel;
          }

          if (
            isAnonymousExportResponse(optionLabel) ||
            isAnonymousExportResponse(responseAnswer)
          ) {
            return;
          }

          bodyRows.push([
            respondentId,
            questionId,
            questionTitle,
            exportType,
            optionLabel,
            responseAnswer,
          ]);
        });

        return;
      }

      if (isOptionExpandable && optionList.length === 0) {
        const { selections, otherValue } = getCheckboxSelections(
          input.rawValue != null ? input.rawValue : input.userResponse,
        );
        const answer =
          otherValue ||
          (selections.length > 0 ? selections.join(", ") : "") ||
          (isSlider
            ? getSimpleExportCellValue(input, formatResponseValue)
            : "") ||
          "";

        const simpleAnswer =
          answer === EMPTY_EXPORT_VALUE || answer === "" ? null : answer;

        if (isAnonymousExportResponse(simpleAnswer)) {
          return;
        }

        bodyRows.push([
          respondentId,
          questionId,
          questionTitle,
          exportType,
          null,
          simpleAnswer,
        ]);
        return;
      }

      const cellValue = getSimpleExportCellValue(input, formatResponseValue);

      if (isMediaInputType(normalizedType) || exportType === "video") {
        if (!isUsableTranscriptionText(cellValue)) {
          return;
        }
      }

      const answer =
        cellValue === EMPTY_EXPORT_VALUE || cellValue == null
          ? null
          : cellValue;

      if (isAnonymousExportResponse(answer)) {
        return;
      }

      const leaveResponseOptionEmpty =
        [
          "name",
          "email",
          "text",
          "paragraph",
          "date",
          "number",
          "file",
          "multiple-files",
          "video",
          "radio-list",
          "checkbox-list",
        ].includes(exportType) ||
        [
          "name",
          "email",
          "text",
          "paragraph",
          "date",
          "number",
          "file",
          "multiple-files",
          "video",
          "record-video",
          "media",
          "audio",
          "radio-list",
          "checkbox-list",
        ].includes(normalizedType);

      const mirrorInOption =
        !leaveResponseOptionEmpty &&
        (exportType === "file" ||
          exportType === "multiple-files" ||
          exportType === "date" ||
          exportType === "number");

      if (exportType === "terms" || exportType === "anonymous") {
        return;
      }

      bodyRows.push([
        respondentId,
        questionId,
        questionTitle,
        exportType,
        mirrorInOption ? answer : null,
        answer,
      ]);
    });
  });

  return {
    headers,
    bodyRows,
    csvRows: [headers, ...bodyRows],
  };
};

/**
 * Question-info rows (one row per question input):
 * question, input field name, type, response_options
 */
export const buildQuestionInfoRecords = (questionRows = []) => {
  const records = [];

  const sortedQuestions = [...questionRows].sort((a, b) =>
    compareNumericValues(a.questionNumber, b.questionNumber),
  );

  sortedQuestions.forEach((row, rowIndex) => {
    const question = `question ${row.questionNumber}`;
    const inputs =
      Array.isArray(row.responseInputs) && row.responseInputs.length > 0
        ? row.responseInputs
        : [
            {
              title: row.questionTitle || "",
              inputTitle: row.inputTitle || row.questionTitle || "",
              type: row.input_type || row.type || "",
              inputOptions: row.inputOptions || "",
              input_options: row.input_options,
              scale_type: row.scale_type,
              slider_config: row.slider_config,
              show_emojis: row.show_emojis,
              show_only_emojis: row.show_only_emojis,
            },
          ];

    inputs.forEach((input, inputIndex) => {
      const subquestion =
        String(input.inputTitle || input.title || "").trim() ||
        EMPTY_EXPORT_VALUE;

      records.push({
        key: input.key || `${row.key || rowIndex}-${inputIndex}`,
        question,
        questionNumber: row.questionNumber,
        subquestion,
        type: input.type || input.input_type || "",
        input_type: input.type || input.input_type || "",
        response_options: input.inputOptions || "",
        inputOptions: input.inputOptions || "",
        input_options: input.input_options,
        scale_type: input.scale_type,
        slider_config: input.slider_config,
        show_emojis: input.show_emojis,
        show_only_emojis: input.show_only_emojis,
      });
    });
  });

  return records;
};

export const buildQuestionInfoCsvRows = (questionRows = []) => {
  const headers = ["question", "input field name", "type", "response_options"];
  const records = buildQuestionInfoRecords(questionRows);
  const bodyRows = records.map((record) => [
    record.question,
    record.subquestion || EMPTY_EXPORT_VALUE,
    record.type,
    record.response_options,
  ]);

  return {
    headers,
    bodyRows,
    records,
    csvRows: [headers, ...bodyRows],
  };
};

export const buildMatrixPdfColumnStyles = (
  columnCount,
  pageWidth,
  margin = 14,
  { firstColumnWidth = 48 } = {},
) => {
  const tableWidth = pageWidth - margin * 2;
  const dataColumnCount = Math.max(columnCount - 1, 0);

  if (columnCount <= 1) {
    return {
      0: {
        cellWidth: tableWidth,
        halign: "left",
        valign: "middle",
        fontStyle: "bold",
      },
    };
  }

  const firstWidth = Math.min(firstColumnWidth, tableWidth * 0.4);
  const dataColWidth = (tableWidth - firstWidth) / dataColumnCount;

  const columnStyles = {
    0: {
      cellWidth: firstWidth,
      halign: "left",
      valign: "middle",
      fontStyle: "bold",
    },
  };

  for (let index = 1; index < columnCount; index += 1) {
    columnStyles[index] = {
      cellWidth: dataColWidth,
      halign: "center",
      valign: "middle",
    };
  }

  return columnStyles;
};

export const formatMatrixPdfHeader = (header = "") => {
  const text = String(header || "").trim();

  if (!text) return text;

  const match = text.match(/^(.+?\(\d+\))\s+(.+)$/);

  if (match) {
    return `${match[1]}\n${match[2]}`;
  }

  return text;
};

export const formatMatrixUserExportValue = (row = {}) => {
  const userLabel = String(row.user || row.respondentId || "").trim();
  const email = row.email;

  if (email && email !== "N/A" && String(email).trim() && userLabel) {
    return `${userLabel}\n${String(email).trim()}`;
  }

  return userLabel || EMPTY_EXPORT_VALUE;
};

export const buildMatrixTableCsvRows = (
  columns = [],
  rows = [],
  { emptyValue = EMPTY_EXPORT_VALUE, formatUserCell = false } = {},
) => {
  const exportColumns = (columns || []).filter(
    (column) => column?.dataIndex != null && column?.title != null,
  );

  const headers = exportColumns.map((column) => String(column.title));

  const bodyRows = (rows || []).map((row) =>
    exportColumns.map((column) => {
      if (formatUserCell && column.dataIndex === "user") {
        const formatted = formatMatrixUserExportValue(row);
        return formatted || emptyValue;
      }

      const value = row[column.dataIndex];

      if (value == null || value === "") {
        return emptyValue;
      }

      return String(value);
    }),
  );

  return {
    headers,
    bodyRows,
    csvRows: [headers, ...bodyRows],
    columnCount: exportColumns.length,
  };
};

export const buildGroupedExportRows = (rows = [], formatResponseValue) => {
  const exportRows = [];

  const toExportResponse = (userResponse, sliderInfo) =>
    toExportResponseValue(userResponse, sliderInfo, formatResponseValue);

  rows.forEach((row) => {
    const responseInputs =
      Array.isArray(row.responseInputs) && row.responseInputs.length > 0
        ? row.responseInputs
        : [
            {
              title: "",
              type: row.type,
              userResponse: row.userResponse,
              sliderInfo: row.sliderInfo || null,
            },
          ];

    if (responseInputs.length > 1) {
      exportRows.push({
        name: row.name,
        email: row.email,
        questionNumber: row.questionNumber,
        questionTitle: row.questionTitle,
        inputTitle: "",
        type: "",
        userResponse: "",
      });

      responseInputs.forEach((input) => {
        exportRows.push({
          name: "",
          email: "",
          questionNumber: "",
          questionTitle: "",
          inputTitle: input.title || "",
          type: input.type || "",
          userResponse: toExportResponse(input.userResponse, input.sliderInfo),
        });
      });

      return;
    }

    const onlyInput = responseInputs[0];
    exportRows.push({
      name: row.name,
      email: row.email,
      questionNumber: row.questionNumber,
      questionTitle: row.questionTitle,
      inputTitle: onlyInput.title || "",
      type: onlyInput.type || row.type || "",
      userResponse: toExportResponse(
        onlyInput.userResponse,
        onlyInput.sliderInfo,
      ),
    });
  });

  return exportRows;
};
